import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import { credentialStatusLabel } from "../../platform/locale";

type QueueRow = {
  request_id: string;
  request_status: string;
  request_version: number;
  requested_at: string;
  approved_at?: string | null;
  decision_reason?: string | null;
  failure_code?: string | null;
  failure_detail?: string | null;
  learner_id: string;
  learner_name: string;
  school_id?: string | null;
  school_name?: string | null;
  achievement_id: string;
  achievement_name: string;
  achievement_status: string;
  credential_definition_id: string;
  credential_name: string;
  issuer_organization_id: string;
  issuer_name: string;
  delivery_id?: string | null;
  delivery_status?: string | null;
  delivery_attempt_count?: number | null;
  delivery_max_attempts?: number | null;
  delivery_updated_at?: string | null;
};

type Detail = {
  request: {
    requestId: string;
    status: string;
    version: number;
    createdAt: string;
    approvedAt?: string | null;
    decisionReason?: string | null;
    failureCode?: string | null;
    failureDetail?: string | null;
  };
  learner: {
    learnerId: string;
    displayName: string;
    schoolName?: string | null;
  };
  achievement: {
    achievementId: string;
    name: string;
    status: string;
    confirmedAt?: string | null;
    criteriaSnapshot: unknown;
  };
  credentialDefinition: {
    definitionId: string;
    name: string;
    description?: string | null;
    externalProfile: string;
    issuerOrganizationId: string;
    issuerName: string;
  };
  evidence: Array<{
    evidenceId: string;
    title: string;
    type: string;
    status: string;
    verifiedAt?: string | null;
  }>;
  evaluations: Array<{
    evaluationResultId: string;
    definitionName: string;
    status: string;
    normalizedScore?: number | null;
    confirmedAt?: string | null;
  }>;
  events: Array<{
    sequence: number;
    fromStatus?: string | null;
    toStatus: string;
    reason?: string | null;
    occurredAt: string;
  }>;
  delivery?: {
    deliveryId: string;
    status: string;
    attemptCount: number;
    maxAttempts: number;
    nextAttemptAt?: string | null;
    updatedAt: string;
  } | null;
  credential?: {
    credentialId: string;
    status: string;
    externalCredentialId: string;
    artifactHash: string;
    verificationUri?: string | null;
    issuedAt: string;
    expiresAt?: string | null;
  } | null;
};

function requestStatusLabel(status:string):string{
  const map:Record<string,string>={
    REQUESTED:"검토 요청",
    APPROVED:"승인·Issuer 대기",
    ISSUED:"발급 완료",
    FAILED:"처리 실패",
    REJECTED:"반려",
    CANCELLED:"취소"
  };
  return map[status] || status;
}

function deliveryStatusLabel(status?:string|null):string{
  if(!status) return "미생성";
  const map:Record<string,string>={
    PENDING:"전송 대기",
    PROCESSING:"처리 중",
    RETRY:"재시도 대기",
    SUCCEEDED:"전송 완료",
    DEAD_LETTER:"수동 복구 필요",
    CANCELLED:"전송 취소"
  };
  return map[status] || status;
}

function formatDate(value?:string|null):string{
  if(!value) return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",
    month:"2-digit",
    day:"2-digit",
    hour:"2-digit",
    minute:"2-digit"
  });
}

export function AdminCredentialQueue({
  client
}:{
  client:PlatformClient;
}){
  const [rows,setRows]=useState<QueueRow[]>([]);
  const [selectedId,setSelectedId]=useState("");
  const [detail,setDetail]=useState<Detail|null>(null);
  const [loading,setLoading]=useState(true);
  const [detailLoading,setDetailLoading]=useState(false);
  const [error,setError]=useState("");
  const [busy,setBusy]=useState("");
  const [reason,setReason]=useState("기준·증빙·평가 결과를 확인했습니다.");
  const [refreshKey,setRefreshKey]=useState(0);
  const [filter,setFilter]=useState("ALL");

  async function loadQueue(){
    setLoading(true);
    setError("");

    const {data,error}=await client.rpc("admin_credential_queue",{
      p_status:filter==="ALL" ? null : filter
    });

    if(error){
      setError(error.message);
      setLoading(false);
      return;
    }

    const next=(data || []) as QueueRow[];
    setRows(next);
    setSelectedId((current)=>
      next.some((item)=>item.request_id===current)
        ? current
        : next[0]?.request_id || ""
    );
    setLoading(false);
  }

  async function loadDetail(id:string){
    if(!id){
      setDetail(null);
      return;
    }

    setDetailLoading(true);
    const {data,error}=await client.rpc("admin_credential_request_detail",{
      p_request_id:id
    });

    if(error){
      setError(error.message);
      setDetail(null);
      setDetailLoading(false);
      return;
    }

    setDetail(data as Detail);
    setDetailLoading(false);
  }

  useEffect(()=>{ void loadQueue(); },[client,refreshKey,filter]);
  useEffect(()=>{ void loadDetail(selectedId); },[client,selectedId,refreshKey]);

  const counts=useMemo(()=>{
    const result:Record<string,number>={};
    for(const row of rows) result[row.request_status]=(result[row.request_status] || 0)+1;
    return result;
  },[rows]);

  async function run(
    key:string,
    action:()=>PromiseLike<{error?:{message:string}|null}>
  ){
    setBusy(key);
    setError("");
    try{
      const result=await action();
      if(result.error) throw new Error(result.error.message);
      setRefreshKey((value)=>value+1);
    }catch(err){
      setError(err instanceof Error ? err.message : "처리 중 오류가 발생했습니다.");
    }finally{
      setBusy("");
    }
  }

  async function triggerIssuer(requestId:string){
    const {data,error}=await client.auth.getSession();
    if(error || !data.session?.access_token){
      throw new Error("로그인 세션을 확인할 수 없습니다.");
    }

    const response=await fetch("/issuer/process",{
      method:"POST",
      headers:{
        "content-type":"application/json",
        "authorization":`Bearer ${data.session.access_token}`
      },
      body:JSON.stringify({requestId})
    });

    const body=await response.json().catch(()=>({}));

    if(!response.ok){
      throw new Error(body?.message || "Issuer 처리를 시작하지 못했습니다.");
    }

    return body;
  }

  async function approveAndProcess(detail:Detail){
    const result=await client.rpc("admin_approve_credential_request",{
      p_request_id:detail.request.requestId,
      p_expected_version:detail.request.version,
      p_reason:reason.trim() || "발급 기준 확인"
    });

    if(result.error) return {error:result.error};

    try{
      await triggerIssuer(detail.request.requestId);
      return {error:null};
    }catch(err){
      return {error:{message:err instanceof Error ? err.message : "Issuer 처리 호출 실패"}};
    }
  }

  async function retryAndProcess(detail:Detail){
    const result=await client.rpc("admin_retry_credential_request",{
      p_request_id:detail.request.requestId,
      p_expected_version:detail.request.version,
      p_reason:reason.trim() || "Issuer 재처리 승인"
    });

    if(result.error) return {error:result.error};

    try{
      await triggerIssuer(detail.request.requestId);
      return {error:null};
    }catch(err){
      return {error:{message:err instanceof Error ? err.message : "Issuer 재처리 호출 실패"}};
    }
  }

  return (
    <section className="admin-credential-workspace">
      <div className="section-heading">
        <div>
          <p className="eyebrow">발급 운영</p>
          <h2>디지털 자격증명 발급 큐</h2>
          <p className="route-description">
            Center의 발급 요청을 검토하고 승인한 뒤 Issuer 처리 상태까지 추적합니다.
          </p>
        </div>
        <span className="phase-badge">BUILD-04</span>
      </div>

      <div className="queue-filter">
        {[
          ["ALL","전체"],
          ["REQUESTED","검토 요청"],
          ["APPROVED","승인"],
          ["FAILED","실패"],
          ["ISSUED","발급 완료"],
          ["REJECTED","반려"]
        ].map(([key,label])=>(
          <button
            key={key}
            className={filter===key ? "filter-chip active" : "filter-chip"}
            onClick={()=>setFilter(key)}
          >
            {label}{key!=="ALL" && counts[key] ? ` ${counts[key]}` : ""}
          </button>
        ))}
      </div>

      {error && <p className="error">{error}</p>}

      <div className="credential-queue-layout">
        <aside className="credential-queue-list">
          <div className="panel-title">
            <strong>발급 요청</strong>
            <span>{rows.length}건</span>
          </div>

          {loading ? (
            <p className="muted">발급 요청을 불러오는 중…</p>
          ) : rows.length===0 ? (
            <div className="empty-inline">
              <strong>해당 상태의 발급 요청이 없습니다.</strong>
            </div>
          ) : (
            <div className="queue-items">
              {rows.map((row)=>(
                <button
                  key={row.request_id}
                  className={selectedId===row.request_id ? "queue-item active" : "queue-item"}
                  onClick={()=>setSelectedId(row.request_id)}
                >
                  <div className="queue-item-top">
                    <strong>{row.learner_name}</strong>
                    <span className={`status-pill queue-${row.request_status.toLowerCase()}`}>
                      {requestStatusLabel(row.request_status)}
                    </span>
                  </div>
                  <span>{row.credential_name}</span>
                  <small>{row.school_name || "학교 미확인"} · {formatDate(row.requested_at)}</small>
                </button>
              ))}
            </div>
          )}
        </aside>

        <div className="credential-queue-detail">
          {!selectedId ? (
            <div className="empty-inline large">
              <strong>발급 요청을 선택하세요.</strong>
            </div>
          ) : detailLoading || !detail ? (
            <p className="muted">발급 검토 정보를 불러오는 중…</p>
          ) : (
            <CredentialRequestDetail
              detail={detail}
              reason={reason}
              setReason={setReason}
              busy={busy}
              onApprove={()=>run("approve",()=>approveAndProcess(detail))}
              onReject={()=>run("reject",()=>client.rpc("admin_reject_credential_request",{
                p_request_id:detail.request.requestId,
                p_expected_version:detail.request.version,
                p_reason:reason.trim() || "발급 요청 반려"
              }))}
              onRetry={()=>run("retry",()=>retryAndProcess(detail))}
              onProcess={()=>run("process",async()=>{
                try{
                  await triggerIssuer(detail.request.requestId);
                  return {error:null};
                }catch(err){
                  return {error:{message:err instanceof Error ? err.message : "Issuer 처리 호출 실패"}};
                }
              })}
            />
          )}
        </div>
      </div>
    </section>
  );
}

function CredentialRequestDetail({
  detail,
  reason,
  setReason,
  busy,
  onApprove,
  onReject,
  onRetry,
  onProcess
}:{
  detail:Detail;
  reason:string;
  setReason(value:string):void;
  busy:string;
  onApprove():void;
  onReject():void;
  onRetry():void;
  onProcess():void;
}){
  const request=detail.request;

  return (
    <>
      <header className="credential-review-header">
        <div>
          <p className="eyebrow">발급 요청 검토</p>
          <h2>{detail.learner.displayName}</h2>
          <p>{detail.learner.schoolName || "학교 미확인"} · {detail.credentialDefinition.name}</p>
        </div>
        <span className={`status-pill queue-${request.status.toLowerCase()}`}>
          {requestStatusLabel(request.status)}
        </span>
      </header>

      <div className="review-summary-grid">
        <Summary label="성취" value={detail.achievement.name} />
        <Summary label="성취 상태" value={detail.achievement.status==="CONFIRMED" ? "확정" : detail.achievement.status} />
        <Summary label="발급 프로필" value={detail.credentialDefinition.externalProfile} />
        <Summary label="발급기관" value={detail.credentialDefinition.issuerName} />
      </div>

      {request.status==="REQUESTED" && (
        <section className="review-action-card">
          <div>
            <strong>사람의 최종 검토</strong>
            <p>증빙과 평가 결과를 확인한 뒤 승인 또는 반려합니다.</p>
          </div>
          <textarea
            value={reason}
            onChange={(event)=>setReason(event.target.value)}
            rows={3}
            placeholder="검토 사유"
          />
          <div className="review-action-row">
            <button className="secondary danger" disabled={busy!==""} onClick={onReject}>
              {busy==="reject" ? "반려 중…" : "반려"}
            </button>
            <button className="primary compact-button" disabled={busy!==""} onClick={onApprove}>
              {busy==="approve" ? "승인 중…" : "발급 승인"}
            </button>
          </div>
        </section>
      )}

      {request.status==="FAILED" && (
        <section className="review-action-card">
          <div>
            <strong>Issuer 처리 실패</strong>
            <p>{request.failureCode || "실패 코드 없음"} · {request.failureDetail || "상세 없음"}</p>
          </div>
          <textarea
            value={reason}
            onChange={(event)=>setReason(event.target.value)}
            rows={2}
            placeholder="재시도 사유"
          />
          <div className="review-action-row">
            <button className="primary compact-button" disabled={busy!==""} onClick={onRetry}>
              {busy==="retry" ? "재시도 중…" : "Issuer 재시도"}
            </button>
          </div>
        </section>
      )}

      {request.status==="APPROVED" && (
        <section className="issuer-status-card">
          <div>
            <p className="eyebrow">Issuer Delivery</p>
            <h3>{deliveryStatusLabel(detail.delivery?.status)}</h3>
            <p>
              {detail.delivery
                ? `시도 ${detail.delivery.attemptCount}/${detail.delivery.maxAttempts} · ${formatDate(detail.delivery.updatedAt)}`
                : "승인 이벤트에서 Issuer 전송 큐를 생성하는 중입니다."}
            </p>
          </div>
          <div className="issuer-status-actions">
            <span className="phase-badge">issuer.open-badges-3</span>
            <button className="secondary" disabled={busy!==""} onClick={onProcess}>
              {busy==="process" ? "처리 중…" : "Issuer 처리 실행"}
            </button>
          </div>
        </section>
      )}

      {request.status==="ISSUED" && detail.credential && (
        <section className="issuer-status-card issued">
          <div>
            <p className="eyebrow">발급 완료</p>
            <h3>{credentialStatusLabel(detail.credential.status,detail.credential.status==="ACTIVE")}</h3>
            <p>{detail.credential.externalCredentialId}</p>
          </div>
          {detail.credential.verificationUri && (
            <a className="verify-link-button" href={detail.credential.verificationUri} target="_blank" rel="noreferrer">
              공개 검증
            </a>
          )}
        </section>
      )}

      {request.status==="REJECTED" && (
        <section className="issuer-status-card rejected">
          <div>
            <p className="eyebrow">반려 완료</p>
            <h3>발급 요청 종료</h3>
            <p>{request.decisionReason || "반려 사유 없음"}</p>
          </div>
        </section>
      )}

      <div className="review-columns">
        <ReviewSection title="검증된 증빙">
          {detail.evidence.length ? detail.evidence.map((item)=>(
            <div className="review-line" key={item.evidenceId}>
              <div>
                <strong>{item.title}</strong>
                <span>{item.type}</span>
              </div>
              <span className={item.status==="VERIFIED" ? "status-pill good" : "status-pill"}>
                {item.status==="VERIFIED" ? "검증 완료" : item.status}
              </span>
            </div>
          )):<p className="case-empty">연결된 증빙이 없습니다.</p>}
        </ReviewSection>

        <ReviewSection title="평가 결과">
          {detail.evaluations.length ? detail.evaluations.map((item)=>(
            <div className="review-line" key={item.evaluationResultId}>
              <div>
                <strong>{item.definitionName}</strong>
                <span>{item.normalizedScore ?? "-"}점</span>
              </div>
              <span className={item.status==="CONFIRMED" ? "status-pill good" : "status-pill"}>
                {item.status==="CONFIRMED" ? "확정" : item.status}
              </span>
            </div>
          )):<p className="case-empty">연결된 평가 결과가 없습니다.</p>}
        </ReviewSection>
      </div>

      <ReviewSection title="처리 이력">
        {detail.events.map((event)=>(
          <div className="review-line timeline" key={event.sequence}>
            <div>
              <strong>{requestStatusLabel(event.toStatus)}</strong>
              <span>{event.reason || "사유 없음"}</span>
            </div>
            <small>{formatDate(event.occurredAt)}</small>
          </div>
        ))}
      </ReviewSection>
    </>
  );
}

function Summary({label,value}:{label:string;value:string}){
  return (
    <div className="review-summary">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ReviewSection({title,children}:{title:string;children:React.ReactNode}){
  return (
    <section className="review-section">
      <h3>{title}</h3>
      <div>{children}</div>
    </section>
  );
}
